import { Badge } from "@deere/fuel-react";
import CampaignIcon from "@mui/icons-material/Campaign";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DirectionsRun from "@mui/icons-material/DirectionsRun";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { TFunction } from "i18next";
import React from "react";
import { NavigateFunction } from "react-router";
import {
  AlertsAndNotification,
  AlertsAndNotificationSource,
} from "../../../../../../../../context/AppNotificationContext/AppNotificationContext";
import { getSurplusReturnTarget } from "../../../../../../../../utils/notificationsUtils";

const SURPLUS_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  surplusReturn:
    "alertsAndNotifications:alertsAndNotifications.surplusType.surplusReturn",
  closeOutReturn:
    "alertsAndNotifications:alertsAndNotifications.surplusType.closeOutReturn",
  programReturn:
    "alertsAndNotifications:alertsAndNotifications.surplusType.programReturn",
};

function getSurplusReturnLabel(surplusType: string, t: TFunction): string {
  const key =
    SURPLUS_TYPE_TRANSLATION_KEYS[surplusType] ??
    "alertsAndNotifications:alertsAndNotifications.surplusType.surplusReturn";
  return t(key);
}

interface NotificationCategoryConfigProps {
  getHeading: (
    notification: AlertsAndNotification,
    t: TFunction,
    isMultipleAccountViewActive: boolean
  ) => string;
  getMessage: (notification: AlertsAndNotification, t: TFunction) => string;
  getButtonText: (t: TFunction) => string;
  onButtonClick: (
    notification: AlertsAndNotification,
    navigate: NavigateFunction,
    markNotificationAsRead: (
      notificationsToMarkAsRead: { id: string; source: string }[]
    ) => void,
    enableDelegateLogin?: boolean
  ) => void;
  getIconBadge: () => React.ReactElement;
}

export const notificationCategoryConfig: Record<
  string,
  NotificationCategoryConfigProps
> = {
  inventoryDiscrepancy: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.inventoryDiscrepancy.withAccountId",
            {
              orderNumber: notification.payload.orderId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.inventoryDiscrepancy",
            {
              orderNumber: notification.payload.orderId,
            }
          ),
    getMessage: (notification, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.inventoryDiscrepancy",
        {
          orderNumber: notification.payload.orderId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.grid.actions.button.inventoryDiscrepancy"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(`/orders/${notification.payload.orderId}`, {
        state: { dealerAccountId: notification.dealerId },
      });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CancelRoundedIcon, {}),
        variant: "error",
      }),
  },
  orderCreation: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.orderCreation.withAccountId",
            {
              orderNumber: notification.payload.orderId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.orderCreation",
            {
              orderNumber: notification.payload.orderId,
            }
          ),
    getMessage: (notification, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.orderCreation",
        {
          orderNumber: notification.payload.orderId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.grid.actions.button.orderCreation"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(`/orders/${notification.payload.orderId}`, {
        state: { dealerAccountId: notification.dealerId },
      });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CheckRoundedIcon, {}),
        variant: "success",
      }),
  },
  dailyReturnCreated: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnCreated.withAccountId",
            {
              returnId: notification.payload.returnId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnCreated",
            {
              returnId: notification.payload.returnId,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.dailyReturnCreated",
        {
          returnId: notification.payload.returnId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.dailyReturnCreated"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(
        `/returns/${notification.payload.returnId}?dealerId=${notification.dealerId}`
      );
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(DirectionsRun, {}),
        variant: "info",
      }),
  },
  dailyReturnCompleted: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnCompleted.withAccountId",
            {
              returnId: notification.payload.returnId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnCompleted",
            {
              returnId: notification.payload.returnId,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.dailyReturnCompleted",
        {
          returnId: notification.payload.returnId,
        }
      ),
    getButtonText: (t: any) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.dailyReturnCompleted"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(
        `/returns/${notification.payload.returnId}?dealerId=${notification.dealerId}`
      );
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CheckCircleRoundedIcon, {}),
        variant: "success",
        type: "bold",
      }),
  },
  dailyReturnRejected: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnRejected.withAccountId",
            {
              returnId: notification.payload.returnId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnRejected",
            {
              returnId: notification.payload.returnId,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.dailyReturnRejected",
        {
          returnId: notification.payload.returnId,
        }
      ),
    getButtonText: (t: any) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.dailyReturnRejected"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(
        `/returns/${notification.payload.returnId}?dealerId=${notification.dealerId}`
      );
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CancelRoundedIcon, {}),
        variant: "error",
      }),
  },
  dailyReturnReadyToShip: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnReadyToShip.withAccountId",
            {
              returnId: notification.payload.returnId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnReadyToShip",
            {
              returnId: notification.payload.returnId,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.dailyReturnReadyToShip",
        {
          returnId: notification.payload.returnId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.dailyReturnReadyToShip"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(
        `/returns/${notification.payload.returnId}?dealerId=${notification.dealerId}`
      );
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(LocalShippingIcon, {}),
        variant: "success",
      }),
  },
  surplusReturnCreated: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnCreated.withAccountId",
            {
              returnId: notification.payload.returnId,
              accountId: notification.dealerId,
              returnType: getSurplusReturnLabel(
                notification.payload.surplusType,
                t
              ),
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnCreated",
            {
              returnId: notification.payload.returnId,
              returnType: getSurplusReturnLabel(
                notification.payload.surplusType,
                t
              ),
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.surplusReturnCreated",
        {
          returnId: notification.payload.returnId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.surplusReturnCreated"
      ),
    onButtonClick: (
      notification,
      navigate,
      markNotificationAsRead,
      enableDelegateLogin
    ) => {
      const target = getSurplusReturnTarget(
        notification.payload.returnId,
        notification.dealerId,
        enableDelegateLogin
      );
      navigate(target.to, { state: target.state });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(DirectionsRun, {}),
        variant: "info",
      }),
  },
  surplusReturnReadyToShip: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnReadyToShip.withAccountId",
            {
              returnId: notification.payload.returnId,
              accountId: notification.dealerId,
              returnType: getSurplusReturnLabel(
                notification.payload.surplusType,
                t
              ),
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnReadyToShip",
            {
              returnId: notification.payload.returnId,
              returnType: getSurplusReturnLabel(
                notification.payload.surplusType,
                t
              ),
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.surplusReturnReadyToShip",
        {
          returnId: notification.payload.returnId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.surplusReturnReadyToShip"
      ),
    onButtonClick: (
      notification,
      navigate,
      markNotificationAsRead,
      enableDelegateLogin
    ) => {
      const target = getSurplusReturnTarget(
        notification.payload.returnId,
        notification.dealerId,
        enableDelegateLogin
      );
      navigate(target.to, { state: target.state });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(LocalShippingIcon, {}),
        variant: "success",
      }),
  },
  surplusReturnCompleted: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnCompleted.withAccountId",
            {
              returnId: notification.payload.returnId,
              accountId: notification.dealerId,
              returnType: getSurplusReturnLabel(
                notification.payload.surplusType,
                t
              ),
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnCompleted",
            {
              returnId: notification.payload.returnId,
              returnType: getSurplusReturnLabel(
                notification.payload.surplusType,
                t
              ),
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.surplusReturnCompleted",
        {
          returnId: notification.payload.returnId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.surplusReturnCompleted"
      ),
    onButtonClick: (
      notification,
      navigate,
      markNotificationAsRead,
      enableDelegateLogin
    ) => {
      const target = getSurplusReturnTarget(
        notification.payload.returnId,
        notification.dealerId,
        enableDelegateLogin
      );
      navigate(target.to, { state: target.state });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CheckCircleRoundedIcon, {}),
        variant: "success",
        type: "bold",
      }),
  },
  claimsRejected: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.claimsRejected.withAccountId",
            {
              claimId: notification.payload.claimId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.claimsRejected",
            {
              claimId: notification.payload.claimId,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.claimsRejected",
        {
          claimId: notification.payload.claimId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.claimsRejected"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(`/claims/${notification.payload.claimId}`, {
        state: { dealerAccountId: notification.dealerId },
      });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CancelRoundedIcon, {}),
        variant: "error",
      }),
  },
  claimsCompleted: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.claimsCompleted.withAccountId",
            {
              claimId: notification.payload.claimId,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.claimsCompleted",
            {
              claimId: notification.payload.claimId,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.claimsCompleted",
        {
          claimId: notification.payload.claimId,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.claimsCompleted"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(`/claims/${notification.payload.claimId}`, {
        state: { dealerAccountId: notification.dealerId },
      });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CheckCircleRoundedIcon, {}),
        variant: "success",
        type: "bold",
      }),
  },
  d2dOrderRequestNeedAction: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dSellerOrderRequests.withAccountId",
            {
              orderId:
                notification.payload.orderId ??
                notification.payload.orderNumber,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dSellerOrderRequests",
            {
              orderId:
                notification.payload.orderId ??
                notification.payload.orderNumber,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.d2dSellerOrderRequests",
        {
          orderId:
            notification.payload.orderId ?? notification.payload.orderNumber,
          partNumber: notification.payload.partNumber,
          orderedQuantity: notification.payload.orderedQuantity,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.d2dSellerOrderRequests"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(`/dealer/${notification.dealerId}/d2d-requests`);
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(NotificationsActiveIcon, {}),
        variant: "warning",
      }),
  },
  d2dOrderAccepted: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dBuyer.withAccountId",
            {
              orderNumber:
                notification.payload.orderId ??
                notification.payload.orderNumber,
              partNumber: notification.payload.partNumber,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dBuyer",
            {
              orderNumber:
                notification.payload.orderId ??
                notification.payload.orderNumber,
              partNumber: notification.payload.partNumber,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.d2dBuyer",
        {
          orderNumber:
            notification.payload.orderId ?? notification.payload.orderNumber,
          partNumber: notification.payload.partNumber,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.grid.actions.button.d2dBuyer"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(`/dealer/${notification.dealerId}/d2d-orders`);
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CheckRoundedIcon, {}),
        variant: "success",
      }),
  },
  d2dOrderShipped: {
    getHeading: (notification, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dShipped.withAccountId",
            {
              orderNumber:
                notification.payload.orderId ??
                notification.payload.orderNumber,
              partNumber: notification.payload.partNumber,
              accountId: notification.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dShipped",
            {
              orderNumber:
                notification.payload.orderId ??
                notification.payload.orderNumber,
              partNumber: notification.payload.partNumber,
            }
          ),
    getMessage: (notification, t): string =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.d2dShipped",
        {
          orderNumber:
            notification.payload.orderId ?? notification.payload.orderNumber,
          partNumber: notification.payload.partNumber,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.grid.actions.button.d2dShipped"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate(`/dealer/${notification.dealerId}/d2d-orders`);
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(LocalShippingIcon, {}),
        variant: "success",
      }),
  },
  broadcastMessageCreated: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getHeading: (notification, t, _isMultipleAccountViewActive) => {
      const { tag } = notification.payload;
      if (!tag) {
        return t(
          "alertsAndNotifications:alertsAndNotifications.cell.title.broadcastCreated.noTag"
        );
      }
      return t(
        "alertsAndNotifications:alertsAndNotifications.cell.title.broadcastCreated",
        {
          title: tag,
        }
      );
    },
    getMessage: (notification, t): string => {
      const { tag } = notification.payload;
      if (!tag) {
        return t(
          "alertsAndNotifications:alertsAndNotifications.cell.description.broadcastCreated.noTag"
        );
      }
      return t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.broadcastCreated",
        {
          title: tag,
        }
      );
    },
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.grid.actions.button.broadcastCreated"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate("/home", {
        state: {
          dealerAccountId: notification.dealerId,
          activeBroadcastMessage: {
            msgId: notification.payload.msgId,
          },
        },
      });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CampaignIcon, {}),
        variant: "info",
      }),
  },
  broadcastMessageUpdated: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getHeading: (notification, t, _isMultipleAccountViewActive) => {
      const { tag } = notification.payload;
      if (!tag) {
        return t(
          "alertsAndNotifications:alertsAndNotifications.cell.title.broadcastUpdated.noTag"
        );
      }
      return t(
        "alertsAndNotifications:alertsAndNotifications.cell.title.broadcastUpdated",
        {
          title: tag,
        }
      );
    },
    getMessage: (notification, t): string => {
      const { tag } = notification.payload;
      if (!tag) {
        return t(
          "alertsAndNotifications:alertsAndNotifications.cell.description.broadcastUpdated.noTag"
        );
      }
      return t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.broadcastUpdated",
        {
          title: tag,
        }
      );
    },
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.grid.actions.button.broadcastUpdated"
      ),
    onButtonClick: (notification, navigate, markNotificationAsRead) => {
      navigate("/home", {
        state: {
          dealerAccountId: notification.dealerId,
          activeBroadcastMessage: {
            msgId: notification.payload.msgId,
          },
        },
      });
      markNotificationAsRead([
        {
          id: notification.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
    getIconBadge: () =>
      React.createElement(Badge, {
        sx: { border: 0 },
        startIcon: React.createElement(CampaignIcon, {}),
        variant: "info",
      }),
  },
};
